import React from "react";
import { Modal } from "react-bootstrap";
import styled, { keyframes, css } from "styled-components";

/**
 * Payment progress modal — shown while the atomic
 * "tokenize → authorize → DB → capture" pipeline is running.
 *
 * Design intent:
 *  - Reassure the user that work is happening (spinner + active step)
 *  - Show ALL 4 phases so they understand the flow has multiple stages
 *  - Static backdrop + no close button — user MUST NOT cancel mid-flight
 *
 * Phases come from useSubmitSubscription:
 *   tokenizing → persisting → done
 * (We collapse "authorizing" and "capturing" under "persisting" since the
 *  backend handles those internally — the user sees them as one step.)
 */
function PaymentProgressModal({ show, phase = "tokenizing", lang = "th" }) {
  const steps = [
    {
      id: "tokenizing",
      label: { th: "เข้ารหัสข้อมูลบัตร", en: "Encrypting card" },
    },
    {
      id: "persisting",
      label: { th: "ประมวลผลการชำระเงิน", en: "Processing payment" },
    },
    {
      id: "done",
      label: { th: "เสร็จสิ้น", en: "Complete" },
    },
  ];

  const currentIndex = steps.findIndex((s) => s.id === phase);
  const safeIndex = currentIndex >= 0 ? currentIndex : 0;

  const headline = {
    tokenizing: { th: "กำลังประมวลผล…", en: "Processing…" },
    persisting: { th: "กำลังสร้างบัญชี…", en: "Creating your account…" },
    done: { th: "สำเร็จ!", en: "Success!" },
  }[phase] ?? { th: "กำลังประมวลผล…", en: "Processing…" };

  return (
    <StyledModal
      show={show}
      backdrop="static"
      keyboard={false}
      centered
      onHide={() => {}}
    >
      <Body>
        <SpinnerWrap>
          <Spinner />
          <SpinnerCore />
        </SpinnerWrap>

        <Headline>{headline[lang]}</Headline>
        <Subline>
          {lang === "en"
            ? "This will only take a moment."
            : "อาจใช้เวลาเล็กน้อย กรุณารอสักครู่"}
        </Subline>

        <Steps>
          {steps.map((step, i) => {
            const isCompleted = i < safeIndex;
            const isCurrent = i === safeIndex && phase !== "done";
            const isFinal = phase === "done" && i === safeIndex;

            let state = "upcoming";
            if (isCompleted || isFinal) state = "completed";
            else if (isCurrent) state = "current";

            return (
              <Step key={step.id}>
                <StepIcon $state={state} aria-hidden>
                  {state === "completed" ? "✓" : state === "current" ? "●" : i + 1}
                </StepIcon>
                <StepLabel $state={state}>{step.label[lang]}</StepLabel>
              </Step>
            );
          })}
        </Steps>

        <Warning>
          {lang === "en"
            ? "Please don't close this window or refresh the page."
            : "กรุณาอย่าปิดหน้าต่างหรือรีเฟรชหน้าจอ"}
        </Warning>
      </Body>
    </StyledModal>
  );
}

export default PaymentProgressModal;

// ─── Animations ──────────────────────────────

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50%      { transform: scale(0.85); opacity: 0.6; }
`;

// ─── Styled ──────────────────────────────────

const StyledModal = styled(Modal)`
  && .modal-dialog {
    max-width: 400px;
  }

  && .modal-content {
    border: none;
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
    overflow: hidden;
    background: #ffffff;
  }
`;

const Body = styled.div`
  padding: 36px 28px 28px;
  text-align: center;
`;

const SpinnerWrap = styled.div`
  position: relative;
  width: 64px;
  height: 64px;
  margin: 0 auto 20px;
`;

/**
 * Outer ring spinner — conic gradient is the cleanest way to make a
 * proper "loading" arc in pure CSS without an SVG.
 */
const Spinner = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: conic-gradient(
    from 0deg,
    transparent 0deg,
    transparent 90deg,
    #6366f1 360deg
  );
  mask: radial-gradient(circle at center, transparent 22px, #000 23px);
  -webkit-mask: radial-gradient(circle at center, transparent 22px, #000 23px);
  animation: ${spin} 1s linear infinite;
`;

/** Inner pulsing dot keeps the eye engaged */
const SpinnerCore = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 14px;
  height: 14px;
  margin: -7px 0 0 -7px;
  border-radius: 50%;
  background: #6366f1;
  animation: ${pulse} 1.4s ease-in-out infinite;
`;

const Headline = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 6px;
`;

const Subline = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 28px;
`;

// ─── Step list ──────────────────────────────

const Steps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #f9fafb;
  border-radius: 10px;
  padding: 16px 20px;
  margin-bottom: 20px;
  text-align: left;
`;

const Step = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const stateStyles = {
  completed: {
    bg: "#10b981",
    color: "#ffffff",
    label: "#374151",
    weight: 500,
  },
  current: {
    bg: "#6366f1",
    color: "#ffffff",
    label: "#111827",
    weight: 600,
  },
  upcoming: {
    bg: "#e5e7eb",
    color: "#9ca3af",
    label: "#9ca3af",
    weight: 400,
  },
};

const StepIcon = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
  transition: all 0.2s;
  background: ${({ $state }) => stateStyles[$state].bg};
  color: ${({ $state }) => stateStyles[$state].color};
  ${({ $state }) =>
    $state === "current" &&
    css`
      box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.15);
      animation: ${pulse} 1.4s ease-in-out infinite;
    `}
`;

const StepLabel = styled.div`
  font-size: 13px;
  color: ${({ $state }) => stateStyles[$state].label};
  font-weight: ${({ $state }) => stateStyles[$state].weight};
  transition: color 0.2s;
`;

// ─── Warning footer ──────────────────────────────

const Warning = styled.div`
  font-size: 11px;
  color: #9ca3af;
  padding: 8px 12px;
  background: #fef3c7;
  color: #92400e;
  border-radius: 6px;
  font-weight: 500;
`;